import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/** Zustand persist key for the app bearer token. SEC-H13: sessionStorage only. */
export const AUTH_STORAGE_KEY = 'agentops-auth-storage';

/**
 * Copies a leftover localStorage auth blob into sessionStorage (once), then
 * deletes the localStorage key so page script cannot read the token there.
 */
export function migrateLegacyAuthStorage(): void {
  try {
    const leftover = localStorage.getItem(AUTH_STORAGE_KEY);
    if (leftover && !sessionStorage.getItem(AUTH_STORAGE_KEY)) {
      sessionStorage.setItem(AUTH_STORAGE_KEY, leftover);
    }
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Storage can throw in locked-down / private contexts.
  }
}

export function readPersistedAuthJson(): string | null {
  try {
    return sessionStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearPersistedAuth(): void {
  try {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // ignore
  }
}

migrateLegacyAuthStorage();

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  isActive: boolean;
  emailVerified: boolean;
  roles: string[];
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: User) => void;
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  logout: () => void;
}

/**
 * Auth store for managing authentication state.
 * B2C-only: token comes from MSAL; no refresh token or expiresAt stored.
 * SEC-H13: persist uses sessionStorage (MSAL cache already does) so the token
 * is not readable from localStorage. HttpOnly cookies stay with SEC-C01.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      setAuth: (token: string, user: User) =>
        set({
          token,
          user,
          isAuthenticated: true,
        }),
      setToken: (token: string | null) =>
        set({ token, isAuthenticated: !!token }),
      setUser: (user: User | null) =>
        set({ user }),
      logout: () => set({
        token: null,
        user: null,
        isAuthenticated: false,
      }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
